import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, {
	ForwardRefRenderFunction,
	forwardRef,
	useImperativeHandle,
	useState,
} from 'react'
import TeamFormationService from '../../../services/team-formation'
import { useDispatch } from 'react-redux'
import { setChallenge } from '../../../redux/actions/challenges'

interface TeamFormationTeamRequirementControlProps {
	teamFormationId: string
	challengeId: string
	editEnabled: boolean
	requirement: {
		options: any
		question: any
		id?: string
	}
}

const TeamFormationTeamRequirementControl: ForwardRefRenderFunction<
	any,
	TeamFormationTeamRequirementControlProps
> = (
	{
		teamFormationId,
		challengeId,
		editEnabled,
		requirement,
	}: TeamFormationTeamRequirementControlProps,
	ref
) => {
	const dispatch = useDispatch()
	const [currentOptions, setCurrentOptions] = useState<any>(
		requirement.options || []
	)

	const getIndex = (id: string) => {
		return currentOptions.findIndex((co) => co.id === id)
	}

	const createOrUpdateRequirement = async (options) => {
		const params = {
			challengeId,
			questionId: requirement.question.id,
			options: options
				.map((o) => {
					return { answerItemId: o.id, maxSlots: o.maxSlots }
				})
				.filter((opt) => opt.maxSlots > 0),
		}
		try {
			const exists = requirement.id
			exists
				? await TeamFormationService.updateTeamRequirement(
						teamFormationId,
						requirement.id,
						params
				  )
				: await TeamFormationService.createTeamRequirements(
						teamFormationId,
						params
				  )

			/**
			 * In stead applying all changes in the local state,
			 * we do a new fetch on the challenge which will load the updated teamRequirements relations automatically
			 */
			dispatch(setChallenge(challengeId, { include: 'teamRequirements' }))
		} catch (e) {
			console.log(e)
		}
	}

	const increaseOrDecrease = (o: any, action: 'incr' | 'decr') => {
		const index = getIndex(o.id)
		const newOpts = [...currentOptions]
		const newVal =
			action === 'incr'
				? newOpts[index].maxSlots + 1
				: newOpts[index].maxSlots - 1
		newOpts[index] = {
			...newOpts[index],
			maxSlots: newVal,
		}

		createOrUpdateRequirement(newOpts)

		setCurrentOptions(newOpts)
	}

	/**
	 * This method is exposed to it's outside ref, to be able to call a delete action from the parent component
	 */
	useImperativeHandle(
		ref,
		() => {
			return {
				removeAllRequirements: async (item) => {
					await TeamFormationService.removeTeamRequirement(
						teamFormationId,
						item.id
					)
					dispatch(setChallenge(challengeId, { include: 'teamRequirements' }))
					return true
				},
			}
		},
		[]
	)

	return (
		<ul className="flex flex-col">
			{currentOptions.map((o) => (
				<li
					key={o.id}
					className="flex flex-wrap bg-purple-50 bg-opacity-25 items-center border-b py-0 pl-4 pr-1"
				>
					<span className="text-gray-900 w-7/12 block text-sm">{o.label}</span>
					<div className="flex items-center w-5/12 justify-end">
						<span className="text-xs text-gray-600 text-right px-1">
							Student count:
						</span>
						{editEnabled && (
							<button
								type="button"
								onClick={() => increaseOrDecrease(o, 'decr')}
								disabled={o.maxSlots == 0}
								className="text-purple-600 focus:ring-0 text-xl"
							>
								<FontAwesomeIcon icon={faMinus} />
							</button>
						)}
						<span
							className={`text-base text-gray-800 text-center rounded w-8 py-1 ${
								editEnabled ? 'bg-white border' : 'bg-transparent'
							}`}
						>
							{o.maxSlots}
						</span>
						{editEnabled && (
							<button
								type="button"
								onClick={() => increaseOrDecrease(o, 'incr')}
								className="text-purple-600 focus:ring-0 text-xl"
							>
								<FontAwesomeIcon icon={faPlus} />
							</button>
						)}
					</div>
				</li>
			))}
		</ul>
	)
}

export default forwardRef(TeamFormationTeamRequirementControl)
